import type {
    ValidationMessage,
    ValidationSeverity,
} from "src/components/QuestionnaireForm/FormProperty/validationTypes";

type ApiValidationHint = {
    severity?: ValidationSeverity | string;
    ssqId?: string;
    ssqSsqHeadingId?: string;
    parentSsqSsqHeadingId?: string;
    propertyId?: string;
    position?: string;
    ssqVersionStructureId?: string;
    propertyName?: string;
    entity?: string;
    postId?: string;
    "{{value}}"?: string;
    [key: string]: unknown;
};

type ApiValidationViolation = {
    message?: string;
    title?: string;
    severity?: ValidationSeverity | string;
    level?: ValidationSeverity | string;
    type?: string;
    propertyPath?: string;
    code?: string | null;
    parameters?: {
        hint?: ApiValidationHint;
    };
    hint?: ApiValidationHint;
};

type ApiValidationErrorResponse = {
    detail?: string;
    violations?: ApiValidationViolation[];
};

const normalizeViolationSeverity = (
    violation: ApiValidationViolation,
    fallback: ValidationSeverity,
): ValidationSeverity => {
    const rawSeverity = violation?.hint?.severity;

    if (typeof rawSeverity === "string") {
        const normalized = rawSeverity.toLowerCase();

        if (normalized === "warning" || normalized === "warning_with_block" || normalized === "error") {
            return normalized as ValidationSeverity;
        }
    }

    return fallback;
};

export const extractValidationMessages = (
    data: unknown,
    defaultSeverity: ValidationSeverity = "error",
): ValidationMessage[] => {
    if (!data) {
        return [];
    }

    const payload: ApiValidationErrorResponse | undefined =
        Array.isArray(data) ? { violations: data as ApiValidationViolation[] }
        : typeof data === "object" ? (data as ApiValidationErrorResponse)
        : undefined;

    const violations = Array.isArray(payload?.violations) ? payload?.violations : undefined;

    if (Array.isArray(violations) && violations.length > 0) {
        return violations
            .map((violation): ValidationMessage | null => {
                if (!violation) {
                    return null;
                }

                const info = violation.message ?? violation.title;
                const message = typeof info === "string" && info.trim().length > 0 ? info.trim() : "";

                if (message.length === 0) {
                    return null;
                }

                return {
                    message,
                    severity: normalizeViolationSeverity(violation, defaultSeverity),
                    ...(violation.propertyPath ? { propertyPath: violation.propertyPath } : {}),
                    hint: violation.hint ?? violation.parameters?.hint,
                };
            })
            .filter((validationMessage): validationMessage is ValidationMessage => validationMessage !== null);
    }

    if (typeof payload?.detail === "string" && payload.detail.trim().length > 0) {
        return payload.detail
            .split("\n")
            .map((line) => line.trim())
            .filter((line) => line.length > 0)
            .map((line) => ({
                message: line,
                severity: defaultSeverity,
            }));
    }

    return [];
};

export const buildPropertyValidationKey = (
    propertyId: number,
    position: number = 0,
    ssqVersionStructureId?: number | null,
): string => {
    if (ssqVersionStructureId == null) {
        return `${propertyId}_${position}`;
    }

    return `${propertyId}_${ssqVersionStructureId}_${position}`;
};
